import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getPermanentPasses, createPermanentPass, updatePermanentPass, deletePermanentPass, getCompanies } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { 
  Shield, Sun, Moon, LogOut, User, Car, Plus, CalendarIcon, 
  Search, Trash2, Edit, ArrowLeft, Building2, Phone, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

export default function PermanentPassesPage() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [passes, setPasses] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [editPass, setEditPass] = useState(null);
  const [passType, setPassType] = useState('person');
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [form, setForm] = useState({
    company_id: '',
    valid_from: null,
    valid_until: null,
    notes: ''
  });

  const [personData, setPersonData] = useState({
    full_name: '',
    position: '',
    phone: ''
  });

  const [vehicleData, setVehicleData] = useState({
    license_plate: '',
    car_brand: '',
    car_model: '',
    driver_name: '',
    phone: ''
  });

  const canEdit = user?.role === 'orp';

  const loadData = useCallback(async () => {
    try {
      const [passesRes, companiesRes] = await Promise.all([
        getPermanentPasses(),
        getCompanies()
      ]);
      setPasses(passesRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBack = () => {
    if (user?.role === 'admin') navigate('/admin');
    else if (user?.role === 'security') navigate('/security');
    else if (user?.role === 'orp') navigate('/dashboard');
    else navigate('/dashboard');
  };

  const resetForm = () => {
    setForm({ company_id: '', valid_from: null, valid_until: null, notes: '' });
    setPersonData({ full_name: '', position: '', phone: '' });
    setVehicleData({ license_plate: '', car_brand: '', car_model: '', driver_name: '', phone: '' });
    setPassType('person');
    setEditPass(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_id) {
      toast.error('Выберите компанию');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        pass_type: passType,
        company_id: form.company_id,
        valid_from: form.valid_from ? format(form.valid_from, 'yyyy-MM-dd') : null,
        valid_until: form.valid_until ? format(form.valid_until, 'yyyy-MM-dd') : null,
        notes: form.notes || null,
        person_data: passType === 'person' ? personData : null,
        vehicle_data: passType === 'vehicle' ? vehicleData : null
      };

      if (editPass) {
        await updatePermanentPass(editPass.id, data);
        toast.success('Пропуск обновлён');
      } else {
        await createPermanentPass(data);
        toast.success('Пропуск создан');
      }
      resetForm();
      setIsOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (pass) => {
    setEditPass(pass);
    setPassType(pass.pass_type);
    setForm({
      company_id: pass.company_id,
      valid_from: pass.valid_from ? new Date(pass.valid_from) : null,
      valid_until: pass.valid_until ? new Date(pass.valid_until) : null,
      notes: pass.notes || ''
    });
    if (pass.pass_type === 'person') {
      setPersonData(pass.person_data || { full_name: '', position: '', phone: '' });
    } else {
      setVehicleData(pass.vehicle_data || { license_plate: '', car_brand: '', car_model: '', driver_name: '', phone: '' });
    }
    setIsOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить постоянный пропуск?')) return;
    try {
      await deletePermanentPass(id);
      toast.success('Пропуск удалён');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const handleToggleActive = async (pass) => {
    try {
      await updatePermanentPass(pass.id, { active: !pass.active });
      toast.success(pass.active ? 'Пропуск деактивирован' : 'Пропуск активирован');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const filteredPasses = passes.filter(pass => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    
    if (pass.company_name?.toLowerCase().includes(searchLower)) return true;
    if (pass.notes?.toLowerCase().includes(searchLower)) return true;
    
    if (pass.pass_type === 'person') {
      if (pass.person_data?.full_name?.toLowerCase().includes(searchLower)) return true;
      if (pass.person_data?.position?.toLowerCase().includes(searchLower)) return true;
      if (pass.person_data?.phone?.includes(search)) return true;
    } else {
      if (pass.vehicle_data?.license_plate?.toLowerCase().includes(searchLower)) return true;
      if (pass.vehicle_data?.car_brand?.toLowerCase().includes(searchLower)) return true;
      if (pass.vehicle_data?.car_model?.toLowerCase().includes(searchLower)) return true;
      if (pass.vehicle_data?.driver_name?.toLowerCase().includes(searchLower)) return true;
      if (pass.vehicle_data?.phone?.includes(search)) return true;
    }
    
    return false;
  });

  const isExpired = (date) => date ? new Date(date) < new Date() : false;
  const isExpiringSoon = (date) => {
    if (!date) return false;
    const d = new Date(date);
    const now = new Date();
    const diff = (d - now) / (1000 * 60 * 60 * 24);
    return diff > 0 && diff <= 30;
  };
  const isPermanent = (pass) => !pass.valid_until;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={handleBack} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-primary">
                  <Shield className="h-5 w-5 text-primary-foreground" />
                </div>
                <div>
                  <h1 className="text-lg font-black tracking-tighter">РЕЕСТР</h1>
                  <p className="text-xs text-muted-foreground">Постоянные пропуска</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
                {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </Button>
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">{user?.full_name}</span>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Поиск по ФИО, компании, номеру авто..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          {canEdit && (
            <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="add-permanent-pass-btn">
                  <Plus className="h-4 w-4" />
                  Добавить пропуск
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editPass ? 'Редактировать пропуск' : 'Новый постоянный пропуск'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Tabs value={passType} onValueChange={setPassType}>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="person" className="gap-2" disabled={!!editPass}>
                        <User className="h-4 w-4" />
                        Человек
                      </TabsTrigger>
                      <TabsTrigger value="vehicle" className="gap-2" disabled={!!editPass}>
                        <Car className="h-4 w-4" />
                        Автомобиль
                      </TabsTrigger>
                    </TabsList>

                    <div className="space-y-4 mt-4">
                      {/* Company */}
                      <div className="space-y-2">
                        <Label>Компания</Label>
                        <Select value={form.company_id} onValueChange={(v) => setForm({...form, company_id: v})}>
                          <SelectTrigger data-testid="company-select">
                            <SelectValue placeholder="Выберите компанию" />
                          </SelectTrigger>
                          <SelectContent>
                            {companies.filter(c => c.active).map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Validity period */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Действует с</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start font-normal">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {form.valid_from ? format(form.valid_from, 'dd.MM.yyyy') : 'Выберите'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 z-50">
                              <Calendar
                                mode="single"
                                selected={form.valid_from}
                                onSelect={(d) => setForm({...form, valid_from: d})}
                                locale={ru}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-2">
                          <Label>Действует до <span className="text-muted-foreground text-xs">(не указано = бессрочно)</span></Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start font-normal">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {form.valid_until ? format(form.valid_until, 'dd.MM.yyyy') : 'Бессрочно'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 z-50">
                              <div className="p-2 border-b">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="w-full"
                                  onClick={() => setForm({...form, valid_until: null})}
                                >
                                  Бессрочно
                                </Button>
                              </div>
                              <Calendar
                                mode="single"
                                selected={form.valid_until}
                                onSelect={(d) => setForm({...form, valid_until: d})}
                                locale={ru}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>

                      <TabsContent value="person" className="space-y-4 mt-0">
                        <div className="space-y-2">
                          <Label>ФИО</Label>
                          <Input
                            value={personData.full_name}
                            onChange={(e) => setPersonData({...personData, full_name: e.target.value})}
                            required={passType === 'person'}
                            data-testid="person-fullname"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Должность</Label>
                          <Input
                            value={personData.position}
                            onChange={(e) => setPersonData({...personData, position: e.target.value})}
                            data-testid="person-position"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Телефон</Label>
                          <Input
                            type="tel"
                            value={personData.phone}
                            onChange={(e) => setPersonData({...personData, phone: e.target.value})}
                            data-testid="person-phone"
                          />
                        </div>
                      </TabsContent>

                      <TabsContent value="vehicle" className="space-y-4 mt-0">
                        <div className="space-y-2">
                          <Label>Номер автомобиля</Label>
                          <Input
                            value={vehicleData.license_plate}
                            onChange={(e) => setVehicleData({...vehicleData, license_plate: e.target.value.toUpperCase()})}
                            required={passType === 'vehicle'}
                            className="font-mono uppercase"
                            data-testid="vehicle-plate"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Марка</Label>
                            <Input
                              value={vehicleData.car_brand}
                              onChange={(e) => setVehicleData({...vehicleData, car_brand: e.target.value})}
                              required={passType === 'vehicle'}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Модель</Label>
                            <Input
                              value={vehicleData.car_model}
                              onChange={(e) => setVehicleData({...vehicleData, car_model: e.target.value})}
                              required={passType === 'vehicle'}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Водитель</Label>
                          <Input
                            value={vehicleData.driver_name}
                            onChange={(e) => setVehicleData({...vehicleData, driver_name: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Телефон</Label>
                          <Input
                            type="tel"
                            value={vehicleData.phone}
                            onChange={(e) => setVehicleData({...vehicleData, phone: e.target.value})}
                          />
                        </div>
                      </TabsContent>

                      <div className="space-y-2">
                        <Label>Примечание</Label>
                        <Textarea
                          value={form.notes}
                          onChange={(e) => setForm({...form, notes: e.target.value})}
                          rows={2}
                        />
                      </div>
                    </div>
                  </Tabs>

                  <DialogFooter>
                    <DialogClose asChild>
                      <Button type="button" variant="outline">Отмена</Button>
                    </DialogClose>
                    <Button type="submit" disabled={submitting}>
                      {submitting ? 'Сохранение...' : 'Сохранить'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Всего пропусков</p>
              <p className="text-2xl font-bold">{passes.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Активных</p>
              <p className="text-2xl font-bold text-emerald-500">{passes.filter(p => p.active && !isExpired(p.valid_until)).length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Бессрочных</p>
              <p className="text-2xl font-bold text-blue-500">{passes.filter(p => isPermanent(p)).length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Просрочено</p>
              <p className="text-2xl font-bold text-destructive">{passes.filter(p => isExpired(p.valid_until)).length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Passes List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Постоянные пропуска</span>
              <Badge variant="secondary">{filteredPasses.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[calc(100vh-450px)]">
              {filteredPasses.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  {search ? 'Ничего не найдено' : 'Нет постоянных пропусков'}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredPasses.map((pass, index) => (
                    <Card 
                      key={pass.id}
                      className={`animate-fade-in ${!pass.active ? 'opacity-60' : ''} ${
                        !isPermanent(pass) && isExpired(pass.valid_until) ? 'border-destructive/50' :
                        !isPermanent(pass) && isExpiringSoon(pass.valid_until) ? 'border-amber-500/50' :
                        isPermanent(pass) && pass.active ? 'border-blue-500/50' : ''
                      }`}
                      style={{ animationDelay: `${index * 0.03}s` }}
                      data-testid={`permanent-pass-${pass.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className={`p-2 rounded-md ${pass.pass_type === 'person' ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                              {pass.pass_type === 'person' ? (
                                <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                              ) : (
                                <Car className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                              )}
                            </div>
                            <div className="space-y-1">
                              <div className="font-semibold">
                                {pass.pass_type === 'person' 
                                  ? pass.person_data?.full_name 
                                  : pass.vehicle_data?.license_plate}
                              </div>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Building2 className="h-3 w-3" />
                                {pass.company_name}
                              </div>
                              {pass.pass_type === 'person' && pass.person_data?.position && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Briefcase className="h-3 w-3" />
                                  {pass.person_data.position}
                                </div>
                              )}
                              {pass.pass_type === 'vehicle' && (
                                <div className="text-sm text-muted-foreground">
                                  {pass.vehicle_data?.car_brand} {pass.vehicle_data?.car_model}
                                  {pass.vehicle_data?.driver_name && ` • ${pass.vehicle_data.driver_name}`}
                                </div>
                              )}
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <CalendarIcon className="h-3 w-3" />
                                {pass.valid_from ? format(new Date(pass.valid_from), 'dd.MM.yyyy') : '—'} 
                                {' — '}
                                {pass.valid_until ? format(new Date(pass.valid_until), 'dd.MM.yyyy') : 'Бессрочно'}
                              </div>
                              {pass.notes && (
                                <div className="text-xs text-muted-foreground italic">
                                  {pass.notes}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <div className="flex items-center gap-2">
                              {isPermanent(pass) ? (
                                pass.active ? (
                                  <Badge className="bg-blue-600">Бессрочный</Badge>
                                ) : (
                                  <Badge variant="secondary">Неактивен</Badge>
                                )
                              ) : isExpired(pass.valid_until) ? (
                                <Badge variant="destructive">Просрочен</Badge>
                              ) : isExpiringSoon(pass.valid_until) ? (
                                <Badge className="bg-amber-500">Истекает</Badge>
                              ) : pass.active ? (
                                <Badge className="bg-emerald-600">Активен</Badge>
                              ) : (
                                <Badge variant="secondary">Неактивен</Badge>
                              )}
                            </div>
                            {canEdit && (
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={pass.active}
                                  onCheckedChange={() => handleToggleActive(pass)}
                                  data-testid={`toggle-${pass.id}`}
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(pass)}
                                  data-testid={`edit-${pass.id}`}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => handleDelete(pass.id)}
                                  data-testid={`delete-${pass.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
